import React, { useEffect, useState } from 'react';
import { View, Text, LayoutChangeEvent } from 'react-native';
import Svg, {
  Path,
  Defs,
  LinearGradient,
  Stop,
  Line,
  Text as SvgText,
} from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

interface AreaChartProps {
  data: { date: string; values: number[] }[];
  colors: string[];
  labels?: string[];
  height?: number;
}

import { isWeb } from '@/lib/platform';

const PADDING_WEB = { top: 10, right: 12, bottom: 24, left: 40 };
const PADDING_MOBILE = { top: 10, right: 8, bottom: 24, left: 32 };

export function AreaChart({
  data,
  colors: seriesColors,
  labels,
  height = 200,
}: AreaChartProps) {
  const themeColors = useThemeColors();
  const PADDING = isWeb ? PADDING_WEB : PADDING_MOBILE;
  const [chartWidth, setChartWidth] = useState(0);
  const clipProgress = useSharedValue(0);

  useEffect(() => {
    clipProgress.value = withTiming(1, { duration: 800 });
  }, [data]);

  const onLayout = (e: LayoutChangeEvent) => {
    setChartWidth(e.nativeEvent.layout.width);
  };

  const clipStyle = useAnimatedStyle(() => ({
    width: `${clipProgress.value * 100}%`,
    overflow: 'hidden' as const,
  }));

  const emptyLegendH = labels && labels.length > 0 ? 24 : 0;
  if (!data.length || !chartWidth) {
    return (
      <View onLayout={onLayout} style={{ height: height + emptyLegendH }}>
        <View className="flex-1 items-center justify-center">
          <Text style={{ color: themeColors.text.tertiary }} className="text-xs">
            No data
          </Text>
        </View>
      </View>
    );
  }

  const innerW = chartWidth - PADDING.left - PADDING.right;
  const innerH = height - PADDING.top - PADDING.bottom;

  // Compute stacked max
  const stackedMax = data.reduce((max, d) => {
    const total = d.values.reduce((a, b) => a + b, 0);
    return Math.max(max, total);
  }, 1);

  // Y-axis scale
  const yScale = (v: number) => PADDING.top + innerH - (v / stackedMax) * innerH;
  const xScale = (i: number) => PADDING.left + (i / Math.max(data.length - 1, 1)) * innerW;

  // Build stacked area paths (bottom layer first)
  const numSeries = seriesColors.length;
  const paths: { fill: string; stroke: string; color: string }[] = [];

  for (let s = 0; s < numSeries; s++) {
    // Cumulative bottom = sum of series 0..s-1
    const bottomAt = (i: number) => {
      let sum = 0;
      for (let k = 0; k < s; k++) sum += data[i].values[k] ?? 0;
      return sum;
    };
    // Cumulative top = sum of series 0..s
    const topAt = (i: number) => bottomAt(i) + (data[i].values[s] ?? 0);

    // Top line (left to right)
    let fillD = `M ${xScale(0)} ${yScale(topAt(0))}`;
    let strokeD = `M ${xScale(0)} ${yScale(topAt(0))}`;
    for (let i = 1; i < data.length; i++) {
      fillD += ` L ${xScale(i)} ${yScale(topAt(i))}`;
      strokeD += ` L ${xScale(i)} ${yScale(topAt(i))}`;
    }
    // Bottom line (right to left) — close fill area
    for (let i = data.length - 1; i >= 0; i--) {
      fillD += ` L ${xScale(i)} ${yScale(bottomAt(i))}`;
    }
    fillD += ' Z';

    paths.push({ fill: fillD, stroke: strokeD, color: seriesColors[s] });
  }

  // Grid lines at 25%, 50%, 75%
  const gridLines = [0.25, 0.5, 0.75].map((pct) => ({
    y: yScale(stackedMax * pct),
    label: Math.round(stackedMax * pct).toString(),
  }));

  // X-axis labels: every ~7th date
  const xLabels: { x: number; label: string }[] = [];
  const step = Math.max(Math.floor(data.length / 6), 1);
  for (let i = 0; i < data.length; i += step) {
    const d = new Date(data[i].date);
    const label = `${d.getMonth() + 1}/${d.getDate()}`;
    xLabels.push({ x: xScale(i), label });
  }

  const legendHeight = labels && labels.length > 0 ? 24 : 0;

  return (
    <View onLayout={onLayout} style={{ height: height + legendHeight }}>
      {chartWidth > 0 && (
        <Animated.View style={[{ height }, clipStyle]}>
          <Svg width={chartWidth} height={height}>
            <Defs>
              {paths.map((p, idx) => (
                <LinearGradient key={`grad-${idx}`} id={`grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                  <Stop offset="0" stopColor={p.color} stopOpacity="0.25" />
                  <Stop offset="1" stopColor={p.color} stopOpacity="0.02" />
                </LinearGradient>
              ))}
            </Defs>

            {/* Grid lines */}
            {gridLines.map((g, i) => (
              <React.Fragment key={`grid-${i}`}>
                <Line
                  x1={PADDING.left}
                  y1={g.y}
                  x2={chartWidth - PADDING.right}
                  y2={g.y}
                  stroke={themeColors.border.light}
                  strokeWidth={1}
                  strokeDasharray="4,4"
                />
                <SvgText
                  x={PADDING.left - 6}
                  y={g.y + 4}
                  textAnchor="end"
                  fontSize={10}
                  fill={themeColors.text.tertiary}
                >
                  {g.label}
                </SvgText>
              </React.Fragment>
            ))}

            {/* Y-axis: 0 and max */}
            <SvgText
              x={PADDING.left - 6}
              y={yScale(0) + 4}
              textAnchor="end"
              fontSize={10}
              fill={themeColors.text.tertiary}
            >
              0
            </SvgText>
            <SvgText
              x={PADDING.left - 6}
              y={yScale(stackedMax) + 4}
              textAnchor="end"
              fontSize={10}
              fill={themeColors.text.tertiary}
            >
              {stackedMax}
            </SvgText>

            {/* Area fills + strokes */}
            {paths.map((p, idx) => (
              <React.Fragment key={`area-${idx}`}>
                <Path d={p.fill} fill={`url(#grad-${idx})`} />
                <Path d={p.stroke} fill="none" stroke={p.color} strokeWidth={2} />
              </React.Fragment>
            ))}

            {/* X-axis labels */}
            {xLabels.map((xl, i) => (
              <SvgText
                key={`x-${i}`}
                x={xl.x}
                y={height - 4}
                textAnchor="middle"
                fontSize={10}
                fill={themeColors.text.tertiary}
              >
                {xl.label}
              </SvgText>
            ))}
          </Svg>
        </Animated.View>
      )}

      {/* Legend */}
      {labels && labels.length > 0 && (
        <View
          className="flex-row items-center flex-wrap gap-3 mt-2"
          style={{ marginLeft: PADDING.left }}
        >
          {labels.map((label, i) => (
            <View key={`legend-${i}`} className="flex-row items-center gap-1.5">
              <View
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: seriesColors[i] }}
              />
              <Text style={{ color: themeColors.text.secondary }} className="text-xs" numberOfLines={1}>
                {label}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
